import { useState, useEffect, useRef, useContext } from 'react'
import { contentfulLoader } from '../../core/Image/contentfulLoader'
import { SiteBuilderContext } from '../../../renderer/context/SiteBuilderContext'
import TelusTVMenu from './Images/TelusTVMenu'
import Image from '../../core/Image/Image'
import NextImage from 'next/image'

import {
  FrameContainer,
  TVFrameOffsetContainer,
  FramedTrailer,
  Video,
  CenterTV,
  TileImageContainer,
  MobileFrameOffsetContainer,
  TVFrameContainer,
  MenuPosition,
  CurrentTrailerTVPos,
  OverflowContainer,
  ImageContainer,
  TileText,
  SmallTileImageContainer,
  TVGradient,
  TrailerImage,
  TVLowerImage,
} from './Styles'
import { Spacer, Typography } from '@telus-uds/components-web'

type FramedTrailerProps = {
  isMobile: boolean
  isReducedMotionOn: boolean
  trailerVideo?: string
  trailerVideoFallback?: string
  staticBackground: string
  mainText: string
  secondaryText: string
  tileImages: string[]
  smallTileImages?: string[]
  tileText: string[]
  tileGenre?: string[]
  mobileImage?: string
  mobileImageWidth: number
  mobileImageHeight: number
  lowerImage?: string
  tvLowerImageHeight?: number
  tvLowerImageWidth?: number
  frameImage?: React.ReactNode
}

const AnimatedFramedTrailer = ({
  isMobile,
  isReducedMotionOn,
  trailerVideo,
  trailerVideoFallback,
  staticBackground,
  mainText,
  secondaryText,
  tileImages,
  smallTileImages,
  tileText,
  tileGenre,
  mobileImage,
  mobileImageWidth,
  mobileImageHeight,
  lowerImage,
  tvLowerImageHeight,
  tvLowerImageWidth,
  frameImage,
}: FramedTrailerProps) => {
  const { isWebpSupported } = useContext(SiteBuilderContext)
  const webpLoader = contentfulLoader(isWebpSupported)
  const tvFrameContainerRef = useRef<HTMLDivElement>(null)
  const mobileFrameContainerRef = useRef<HTMLDivElement>(null)
  const decoderRef = useRef<HTMLDivElement>(null)
  const [tvFrameHeight, setTVFrameHeight] = useState(0)
  const [mobileFrameHeight, setMobileFrameHeight] = useState(0)
  const [offsetTopAmount, setOffsetTopAmount] = useState(0)

  useEffect(() => {
    const setContainerHeight = () => {
      if (tvFrameContainerRef.current) {
        setTVFrameHeight(
          tvFrameContainerRef.current.clientHeight + offsetTopAmount + decoderRef.current.clientHeight / 2
        )
      }

      if (mobileFrameContainerRef.current) {
        setMobileFrameHeight(mobileFrameContainerRef.current.clientHeight + offsetTopAmount)
      }
    }

    const resizeObserver = new ResizeObserver(() => {
      setContainerHeight()
      getOffsetTopValue()
    })

    if (document) {
      resizeObserver.observe(document.body)
    }

    setContainerHeight()
    getOffsetTopValue()
  }, [])

  const getOffsetTopValue = () => {
    const tileSpacing = isMobile ? 12 : 25
    const maxTilesPerRow = 7
    const maxTilesPerRowMobile = 3
    const aspectRatio = 0.5625 // 16:9
    const screenWidth = window.document.body.clientWidth
    const maxTiles = isMobile ? maxTilesPerRowMobile - 1 : maxTilesPerRow - 1

    setOffsetTopAmount(-((((screenWidth - tileSpacing * (maxTiles - 1)) / maxTiles) * aspectRatio) / 2))
  }

  const getTilePosition = (idx) => {
    return `${idx * 57.5}%`
  }

  const getSmallTilePosition = (idx) => {
    return `${idx * 37}%`
  }

  const renderTV = () => {
    return (
      <FrameContainer height={tvFrameHeight}>
        <TVFrameOffsetContainer ref={tvFrameContainerRef} top={offsetTopAmount}>
          <TVLowerImage ref={decoderRef}>
            <Image src={lowerImage} height={tvLowerImageHeight} width={tvLowerImageWidth}></Image>
          </TVLowerImage>
          <TVFrameContainer>
            <TVGradient></TVGradient>
            {isReducedMotionOn && <TrailerImage src={staticBackground} alt="" />}
            <div style={{ width: '91.7%' }}>{frameImage}</div>
            <FramedTrailer>
              {!isReducedMotionOn && (
                <Video loop autoPlay playsInline muted loading="lazy" width="100%" height="100%">
                  <source src={trailerVideo} type="video/webm" />
                  <source src={trailerVideoFallback} type="video/mp4" />
                </Video>
              )}
            </FramedTrailer>
            <MenuPosition>
              <TelusTVMenu />
            </MenuPosition>
            <CurrentTrailerTVPos style={{ width: '70%' }} top="18%" left="13.75%">
              <Typography block tokens={{ fontWeight: 700 }} variant={{ size: 'h1', inverse: true }}>
                {mainText}
              </Typography>
            </CurrentTrailerTVPos>
            <CurrentTrailerTVPos style={{ width: '70%' }} top="24%" left="13.75%">
              <Spacer space={3} />
              <Typography block tokens={{ fontWeight: 300 }} variant={{ size: 'medium', inverse: true }}>
                {secondaryText}
              </Typography>
            </CurrentTrailerTVPos>
            <OverflowContainer>
              <CurrentTrailerTVPos top="8%" left="2.75%">
                <ImageContainer>
                  {tileImages &&
                    tileImages.map((image, idx) => {
                      return (
                        <TileImageContainer
                          id={`tileTV${idx}`}
                          left={getTilePosition(idx)}
                          top={idx == 0 ? '32.5%' : '33%'}
                          width={idx == 0 ? '55%' : '50%'}
                          key={idx}
                        >
                          <NextImage loader={webpLoader} src={image} alt="" layout="fill" objectFit="cover" />
                        </TileImageContainer>
                      )
                    })}
                </ImageContainer>
              </CurrentTrailerTVPos>
              <CurrentTrailerTVPos top="10%" left="2.75%">
                <ImageContainer>
                  {tileText &&
                    tileText.map((_, idx) => {
                      return (
                        <TileText
                          id={`tileTVText${idx}`}
                          left={getTilePosition(idx)}
                          top={idx == 0 ? '50.5%' : '49.5%'}
                          width={idx == 0 ? '55%' : '50%'}
                          key={idx}
                        >
                          <Typography block variant={{ size: 'medium', inverse: true }}>
                            {tileText[idx]}
                          </Typography>
                          <Typography
                            block
                            tokens={{ fontWeight: 500, color: '#676e73' }}
                            variant={{ size: 'small', inverse: true }}
                          >
                            {tileGenre[idx]}
                          </Typography>
                        </TileText>
                      )
                    })}
                </ImageContainer>
              </CurrentTrailerTVPos>
              <CurrentTrailerTVPos top="8%" left="4.25%">
                <ImageContainer>
                  {smallTileImages.length &&
                    smallTileImages.map((image, idx) => {
                      return (
                        <SmallTileImageContainer
                          id={`tileTV${idx}`}
                          left={getSmallTilePosition(idx)}
                          top={'65%'}
                          width={idx == 0 ? '55%' : '50%'}
                          key={idx}
                        >
                          <NextImage loader={webpLoader} src={image} alt="" layout="fill" objectFit="cover" />
                        </SmallTileImageContainer>
                      )
                    })}
                </ImageContainer>
              </CurrentTrailerTVPos>
            </OverflowContainer>
          </TVFrameContainer>
        </TVFrameOffsetContainer>
      </FrameContainer>
    )
  }

  const renderMobile = () => {
    return (
      <FrameContainer height={0.5 * mobileFrameHeight}>
        <MobileFrameOffsetContainer ref={mobileFrameContainerRef} top={-0.5 * mobileFrameHeight}>
          {mobileImage && <Image src={mobileImage} alt="" height={mobileImageHeight} width={mobileImageWidth} />}
        </MobileFrameOffsetContainer>
      </FrameContainer>
    )
  }

  return <CenterTV data-testid={'animated-framed-trailer-testid'}>{isMobile ? renderMobile() : renderTV()}</CenterTV>
}

export default AnimatedFramedTrailer
